import { FileHandle, Serializer } from "svelte-pixel-editor-wasm";
import { wasm, wasm_event_callback } from "$lib/wasm/wasm";
import { db, type FileInfo } from "$lib/wasm/db";
import { createInstanceEventHandler } from "$lib/wasm/wasmInstanceEvent";
import { createEvent } from "$lib/utility/event";
import { createDialog, createDialogButton } from "ceyper-common";
import { interaction } from "$lib/state";

/// Types //////////////////////////////////

export type FrameInfo = {
    duration: number;
};
export type LayerInfo = {
    index: number;
    name: string;
    visible: boolean;
};

export type FileData = {
    id: number;
    name: string;
    width: number;
    height: number;
    data: Uint8Array;
    render: Uint32Array;
};
export type FileMetadata = {
    width: number;
    height: number;
    projectBytes: number;
    rawPixelBytes: number;
    frameCount: number;
    layerCount: number;
    undoCount: number;
    redoCount: number;
};

/// Class //////////////////////////////////

const { onEvent, registerInstance } = createInstanceEventHandler<File>();
let idCounter = 1;
wasm_event_callback.file = onEvent;

export class File {
    static #serializer: Serializer | null = null;

    id?: number;
    name: string;

    #file: FileHandle;

    get internal() {
        return this.#file;
    }

    internalIsFree = false;
    width: number;
    height: number;

    #unregister: () => void;

    private constructor(file: FileHandle, name: string, id?: number) {
        this.id = id;
        this.name = name;
        this.#file = file;
        this.width = this.#file.width;
        this.height = this.#file.height;

        this.#file.set_id(idCounter);
        this.#unregister = registerInstance(this, idCounter);
        idCounter++;
    }

    static new(width: number, height: number, name = "New file") {
        return new File(FileHandle.new(width, height), name);
    }

    static fromImageData(image: ImageData, name = "Imported image", layerName = "Layer 1") {
        const data = new Uint32Array(
            image.data.buffer.slice(
                image.data.byteOffset,
                image.data.byteOffset + image.data.byteLength
            )
        );

        return new File(FileHandle.new_from_data(image.width, image.height, data, layerName), name);
    }

    resize(width: number, height: number) {
        interaction.value.resizeFile(width, height);
        this.refreshSize();
    }

    refreshSize() {
        this.width = this.#file.width;
        this.height = this.#file.height;
    }

    static async load(data: FileData) {
        const file = await File.#fromData(data);
        if (!file) return null;

        return new File(file, data.name, data.id);
    }

    free() {
        this.internalIsFree = true;
        this.internal.free();
        this.#file = null as any;
        this.#unregister();
        this.#unregister = null as any;
    }

    printHistory() {
        let past = this.#file.get_history_past();
        let future = this.#file.get_history_future();

        console.log(`History
${past
    .reverse()
    .map((p, i) => `${i.padStart(2)}: ${p}`)
    .slice(0, 10)
    .reverse()
    .join("\n")}
--- Current ---
${future
    .reverse()
    .map((p, i) => `${i.padStart(2)}: ${p}`)
    .join("\n")}
        `);
    }

    getFrames(): FrameInfo[] {
        return this.#file.get_frame_info() as FrameInfo[];
    }

    getLayers(frame: number) {
        return this.#file.get_layer_info(frame) as LayerInfo[];
    }

    getHistory() {
        const past = this.#file.get_history_past().reverse();
        const future = this.#file.get_history_future();

        return {
            past,
            future,
        };
    }

    getSerializedSize() {
        const int = interaction.value.internal;
        const ptr = int.serialize(this.#file);
        return ptr.length as number;
    }

    getMetadata(): FileMetadata {
        const frames = this.getFrames();
        const history = this.getHistory();
        const layerCount = frames.reduce((count, _, frame) => count + this.getLayers(frame).length, 0);

        return {
            width: this.width,
            height: this.height,
            projectBytes: this.getSerializedSize(),
            rawPixelBytes: this.width * this.height * 4 * layerCount,
            frameCount: frames.length,
            layerCount,
            undoCount: history.past.length,
            redoCount: history.future.length,
        };
    }

    readonly onDataChanged = createEvent<[number, number]>("data-changed");
    readonly onSave = createEvent<void>("save");
    readonly onHistoryChanged = createEvent<void>("history-changed");
    readonly onFramesChanged = createEvent<void>("frames-changed");
    readonly onLayersChanged = createEvent<void>("layers-changed");

    #eventHandlers = {
        "frames-changed": this.onFramesChanged.trigger,
        "layers-changed": this.onLayersChanged.trigger,
        "data-changed": this.onDataChanged.trigger,
        execute: this.onHistoryChanged.trigger,
        undo: this.onHistoryChanged.trigger,
        redo: this.onHistoryChanged.trigger,
    };

    receivedEvent(event: string, data: any) {
        ((this.#eventHandlers as any)[event] ?? console.log)(data);
    }

    /// DB Operations

    async save() {
        this.id = await db.putFile(this.toData(true));
        this.onSave.trigger();
    }

    static async delete(file: File | FileInfo | FileData) {
        if (!file.id) return;
        await db.deleteFile(file.id);
    }

    /// Serialization

    toData(clone = false): FileData {
        // todo make sure editing is not in progress here...
        const int = interaction.value.internal;
        const ptr = int.serialize(this.#file);
        let data = new Uint8Array(wasm.memory.buffer, ptr.ptr, ptr.length);
        let render = new Uint32Array(
            wasm.memory.buffer,
            this.#file.render_ptr(0),
            this.width * this.height
        );

        if (clone) {
            data = data.slice();
            render = render.slice();
        }

        return {
            name: this.name,
            id: this.id!,
            width: this.width,
            height: this.height,
            data,
            render,
        };
    }

    static async #fromData(fileData: FileData) {
        const int = interaction.value.internal;
        try {
            const { data } = fileData;
            let ptr = int.deserialize_ptr(data.length);
            const mem = new Uint8Array(wasm.memory.buffer, ptr.ptr, ptr.length);
            mem.set(data);
            return int.deserialize();
        } catch (e) {
            console.error("Failed to deserialize file");
            console.error(e);

            const result = await createDialog({
                id: "load-failed",
                title: "Failed to load file data",
                fields: {
                    info: {
                        name: "Name",
                        type: "text",
                        value: "Do you want to load this file as an image (discards all layers and frames)?",
                    },
                    warning: {
                        name: "Name",
                        type: "warning",
                        value: "This will overwrite the old file and all previous data will be lost.",
                    },
                },
                buttons: [createDialogButton.close("No"), createDialogButton.confirm("Yes")],
            });

            if (!result) {
                return null;
            }

            const { width, height } = fileData;
            let render = fileData.render as Uint32Array | Uint8Array;
            if (render instanceof Uint8Array) {
                render = new Uint32Array(render.buffer);
            }

            return FileHandle.new_from_data(width, height, render, "Layer 1");
        }
    }
}
